import React from 'react';

const SectionTitle = ({ children }) => (
  <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-3">{children}</h2>
);

const Para = ({ children }) => (
  <p className="text-slate-700 leading-relaxed mb-3">{children}</p>
);

export default function PrivacyPolicy() {
  const lastUpdated = 'February 2026';

  return (
    <div className="min-h-screen bg-slate-50" data-testid="privacy-policy-page">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-6 py-6">
          <h1 className="text-3xl font-bold text-slate-900" data-testid="privacy-policy-heading">
            Privacy Policy
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Ezy Mobility Solutions Motorcycle Rental LLC — Last updated {lastUpdated}
          </p>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        <Para>
          This Privacy Policy explains how{' '}
          <strong>Ezy Mobility Solutions Motorcycle Rental LLC</strong> (“Ezy Mobility”, “we”,
          “our”, “us”) collects, uses, stores, and protects information when you interact with our
          services, including our fleet management platform accessible at{' '}
          <a href="https://ezygo.ae" className="text-emerald-700 underline">
            https://ezygo.ae
          </a>{' '}
          and messages sent via our official WhatsApp Business account.
        </Para>

        <SectionTitle>1. Company Details</SectionTitle>
        <Para>
          <strong>Company Name:</strong> Ezy Mobility Solutions Motorcycle Rental LLC
          <br />
          <strong>Registered Address:</strong> Al Showaib Real Estate — Warehouse 7, Plot No 194,
          Amman Street, Al Qusais Industrial Area 3, Dubai, United Arab Emirates
          <br />
          <strong>Website:</strong>{' '}
          <a href="https://ezygo.ae" className="text-emerald-700 underline">
            https://ezygo.ae
          </a>
          <br />
          <strong>WhatsApp Business Number:</strong> +971 50 567 0732
        </Para>

        <SectionTitle>2. Information We Collect</SectionTitle>
        <Para>We collect only the information necessary to operate our services, including:</Para>
        <ul className="list-disc pl-6 text-slate-700 space-y-2 mb-3">
          <li>
            <strong>Contact information:</strong> Full name, mobile phone number, WhatsApp number,
            email address, and Emirates ID number (where applicable).
          </li>
          <li>
            <strong>Employment / rider records:</strong> Job role, employment status, delivery
            platform (Talabat, Noon, Careem, etc.), payroll data, salary advances, and loan
            balances.
          </li>
          <li>
            <strong>Fleet and vehicle data:</strong> Registered vehicles, plates, service history,
            handovers, fines, tolls (Salik), and fuel entries.
          </li>
          <li>
            <strong>WhatsApp communications:</strong> Messages we send you, delivery and read
            receipts returned by WhatsApp, and any replies you send us.
          </li>
          <li>
            <strong>Documents and attachments:</strong> Uploaded proof-of-payment slips, salary
            slips, fines documentation, and identity documents.
          </li>
          <li>
            <strong>Technical data:</strong> Access logs, IP addresses, session tokens, and audit
            trails that record who accessed which record and when.
          </li>
        </ul>

        <SectionTitle>3. Purpose of WhatsApp Messaging</SectionTitle>
        <Para>
          We use the WhatsApp Business Cloud API to send transactional and utility notifications
          only. We do <strong>not</strong> send marketing or promotional messages via WhatsApp.
          Typical messages you may receive from us include:
        </Para>
        <ul className="list-disc pl-6 text-slate-700 space-y-2 mb-3">
          <li>Fine issuance notifications and copies of fine slips.</li>
          <li>Monthly salary slips and payroll disbursement confirmations.</li>
          <li>Formal warnings, disciplinary notices, and policy reminders.</li>
          <li>Loan account statements, arrears alerts, and salary advance confirmations.</li>
          <li>Handover, maintenance, and vehicle-return acknowledgements.</li>
        </ul>
        <Para>
          All WhatsApp templates we use are pre-approved by Meta. You may opt out of WhatsApp
          messaging at any time by replying <strong>STOP</strong> to any of our messages, or by
          contacting us at the email address below.
        </Para>

        <SectionTitle>4. How We Use Your Information</SectionTitle>
        <ul className="list-disc pl-6 text-slate-700 space-y-2 mb-3">
          <li>To onboard riders, staff, and fleet lessees and maintain their records.</li>
          <li>To process payroll, salary advances, and loan repayments.</li>
          <li>To notify you of fines, tolls, and maintenance obligations.</li>
          <li>To comply with UAE regulatory, tax, labour, and traffic-authority requirements.</li>
          <li>To improve internal operations, reporting, and fraud prevention.</li>
        </ul>

        <SectionTitle>5. Data Storage & Security</SectionTitle>
        <Para>
          Your data is stored on secured cloud infrastructure and access is limited to authorised
          personnel of Ezy Mobility Solutions under role-based access controls. We retain personal
          data only for as long as necessary to fulfil the purposes described above and to comply
          with UAE legal, accounting, and audit obligations.
        </Para>
        <Para>
          WhatsApp messages transit through Meta’s end-to-end encrypted infrastructure. Message
          logs (delivery, read, failure) that we receive from Meta are stored in our secured
          database purely for audit and compliance purposes and are never shared with third
          parties for marketing.
        </Para>

        <SectionTitle>6. Sharing of Information</SectionTitle>
        <Para>
          We do not sell, rent, or trade your personal information. We may share limited
          information with:
        </Para>
        <ul className="list-disc pl-6 text-slate-700 space-y-2 mb-3">
          <li>UAE government or regulatory bodies where legally required.</li>
          <li>
            Delivery platform partners (e.g. Talabat, Noon, Careem) strictly for the purpose of
            managing your rider engagement with them.
          </li>
          <li>
            Trusted service providers who help us deliver our services (e.g. WhatsApp/Meta for
            messaging, cloud hosting providers), under confidentiality obligations.
          </li>
        </ul>

        <SectionTitle>7. Your Rights</SectionTitle>
        <Para>You have the right to:</Para>
        <ul className="list-disc pl-6 text-slate-700 space-y-2 mb-3">
          <li>Request a copy of the personal information we hold about you.</li>
          <li>Request correction of any inaccurate or outdated personal information.</li>
          <li>
            Request deletion of your personal information, subject to our legal and contractual
            retention obligations.
          </li>
          <li>Withdraw your consent to receive WhatsApp messages at any time.</li>
          <li>Lodge a complaint about how we handle your personal information.</li>
        </ul>
        <Para>
          To exercise any of these rights, please contact us using the details below. We will
          respond within a reasonable timeframe in line with UAE data protection standards.
        </Para>

        <SectionTitle>8. Changes to this Policy</SectionTitle>
        <Para>
          We may update this Privacy Policy from time to time to reflect changes in our services,
          legal obligations, or industry best practice. The latest version will always be
          available at{' '}
          <a href="https://ezygo.ae/privacy" className="text-emerald-700 underline">
            https://ezygo.ae/privacy
          </a>
          .
        </Para>

        <SectionTitle>9. Contact Us</SectionTitle>
        <Para>
          If you have any questions about this Privacy Policy or how we handle your personal
          information, please contact:
        </Para>
        <div className="bg-white border border-slate-200 rounded-lg p-4 mb-8">
          <p className="text-slate-800">
            <strong>Ezy Mobility Solutions Motorcycle Rental LLC</strong>
            <br />
            Al Showaib Real Estate — Warehouse 7, Plot No 194, Amman Street,
            <br />
            Al Qusais Industrial Area 3, Dubai, United Arab Emirates
            <br />
            Email:{' '}
            <a
              href="mailto:erp@ezytronix.com"
              className="text-emerald-700 underline"
              data-testid="privacy-contact-email"
            >
              erp@ezytronix.com
            </a>
            <br />
            WhatsApp: +971 50 567 0732
            <br />
            Website:{' '}
            <a href="https://ezygo.ae" className="text-emerald-700 underline">
              https://ezygo.ae
            </a>
          </p>
        </div>

        <footer className="border-t border-slate-200 pt-6 pb-12 text-xs text-slate-500">
          © {new Date().getFullYear()} Ezy Mobility Solutions Motorcycle Rental LLC. All rights
          reserved.
        </footer>
      </main>
    </div>
  );
}
